import * as React from 'react'

import { ActionItem, Button, Icon, useClassNames } from '@factorialco/deprecated-design-system'
import filter from 'lodash/filter'
import includes from 'lodash/includes'
import sortBy from 'lodash/sortBy'

import BoxList from 'modules/core/components/BoxList'
import InlineSearch from 'modules/core/components/InlineSearch'
import ScrollableItem from 'modules/core/components/ScrollableItem'
import ScrollableList from 'modules/core/components/ScrollableList'
import SearchWithFilter from 'modules/core/components/SearchWithFilter'
import useSearch from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import * as styles from './index.vanilla.css'

const sortByTypeCriteria = (leaveType: LeaveType): string => {
  if (leaveType.isHoliday) return '0'

  if (leaveType.isMedicalLeave) return '1'

  return `3${leaveType.name}`
}

interface Props {
  all?: string
  autofocus?: boolean
  color?: string
  disabled?: boolean
  leaveTypes: LeaveType[]
  fallback?: string
  label?: string
  none?: string
  hideNone?: boolean
  onSelect: (id: number | null | undefined) => void
  onSelectMultiple: (selectedIds: number[]) => void
  placeholder?: string
  multiselect?: boolean
  selectedIds: number[]
}

const LeaveTypeSelector = ({
  all,
  autofocus,
  color,
  disabled,
  leaveTypes,
  fallback,
  label,
  none,
  hideNone,
  onSelect,
  onSelectMultiple,
  placeholder,
  multiselect,
  selectedIds,
}: Props) => {
  const cx = useClassNames(styles)
  const { search, onSearch } = useSearch()

  const onClick = (leaveType: LeaveType) => {
    return () => {
      if (disabled) return

      onSelect(leaveType.attributes.id)
    }
  }

  const renderBlank = () => {
    return <div className={styles.blank}>{fallback || i18n.t('common.leave_type_not_found')}</div>
  }

  const onSelectAll = () => {
    if (disabled) return

    onSelectMultiple(leaveTypes.map((leave) => leave.attributes.id))
  }

  const onSelectNone = () => {
    if (disabled) return

    onSelectMultiple([])
  }

  const renderLeaveType = (leaveType: LeaveType) => {
    const selected = includes(selectedIds, leaveType.attributes.id)

    return (
      <ScrollableItem key={leaveType.attributes.id}>
        <ActionItem
          disabled={disabled}
          onClick={onClick(leaveType)}
          selected={selected}
          showIcon={false}
        >
          <div
            className={cx('leaveType', {
              selected,
            })}
          >
            <div className={styles.leaveTypeInfo}>
              <div className={styles.name}>{leaveType.name}</div>
            </div>
            <div
              style={
                color && selected
                  ? {
                      background: color,
                      borderColor: color,
                    }
                  : {}
              }
              className={styles.checkbox}
            >
              {selected && <Icon.Small icon="check" color="grey000White" />}
            </div>
          </div>
        </ActionItem>
      </ScrollableItem>
    )
  }

  const renderControls = (filteredLeaveTypes: Array<Record<string, any>>) => {
    if (!multiselect || !onSelectMultiple) return null

    if (!filteredLeaveTypes.length || !!search) return null

    return (
      <div className={styles.controls}>
        <div className={styles.controlsActions}>
          <div className={styles.controlsActionsLabel}>{label || i18n.t('common.select')}:</div>
          <div className={styles.links}>
            <Button onClick={onSelectAll} type="secondary">
              {all || i18n.t('common.all')}
            </Button>
          </div>
          {!hideNone && (
            <div className={styles.links}>
              <Button onClick={onSelectNone} type="secondary">
                {none || i18n.t('common.none')}
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  const sortedLeaveType = (leaveType: LeaveType): string => {
    const isSelected = selectedIds.includes(leaveType.attributes.id)
    const selectedCriteria = isSelected ? '0' : '1'

    return `${selectedCriteria}${sortByTypeCriteria(leaveType)}`
  }

  const filteredLeaveTypes = sortBy(
    filter(leaveTypes, (leaveType) => matchText(search, leaveType.name)),
    sortedLeaveType
  )

  return (
    <BoxList>
      <div className={styles.searchbar}>
        <SearchWithFilter
          inlineSearch={
            <InlineSearch
              autofocus={autofocus}
              placeholder={placeholder}
              onSearch={onSearch}
              search={search}
            />
          }
          noPadding
          noBorder
        />
      </div>
      {multiselect && renderControls(filteredLeaveTypes)}
      <ScrollableList>
        {filteredLeaveTypes.length > 0 ? filteredLeaveTypes.map(renderLeaveType) : renderBlank()}
      </ScrollableList>
    </BoxList>
  )
}

export default LeaveTypeSelector
